import {
	BuildAutomationMode,
	BuildStatus,
	PIPELINE_OPS_DEFAULT_WINDOW_DAYS,
	PipelineAwaitingState,
} from '@cadstrom/cadstrom-shared';
import RefreshIcon from '@mui/icons-material/Refresh';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Fragment, useCallback, useMemo, useState } from 'react';

import { useTRPC } from '#src/trpc.js';
import { HiddenBuilds } from './hidden-builds.js';
import { OrganizationPicker } from './organization-picker.js';
import { PipelineOpsDetail } from './pipeline-ops-detail.js';
import { PipelineOpsGridRow } from './pipeline-ops-grid-row.js';
import { searchSchema } from './pipeline-ops-search.js';
import { formatAge, heartbeatBars, stalledBuild, STATUS_LABELS } from './pipeline-ops-utils.js';
import { PipelineOpsInclusion, PipelineOpsRowAction } from './pipeline-ops-view.types.js';
import { ReviewDialog } from './review-dialog.js';
import { RowSearchBox } from './row-search-box.js';
import { rowMatchesSearch } from './row-search.js';
import { StatTile } from './stat-tile.js';
import { UnlistedRuns } from './unlisted-runs.js';
import { UploadDialog } from './upload-dialog.js';
import { WindowPicker } from './window-picker.js';

import type { GetPipelineOpsSnapshotOutputDto } from '@cadstrom/backend/shared';
import type { PipelineOpsRow } from './pipeline-ops-grid-row.js';

/** Columns in the grid, so an expanded row's panel can span all of them. */
const GRID_COLUMN_COUNT = 13;

/** How often the page re-reads the fleet, in milliseconds. */
const REFETCH_INTERVAL_MS = 20_000;

/** The filters the stat strip can put on the grid. */
enum PipelineOpsFilter {
	All = 'all',
	Error = 'error',
	Demoted = 'demoted',
	FastMode = 'fast_mode',
	Awaiting = 'awaiting',
	Quiet = 'quiet',
	Stalled = 'stalled',
}

export const Route = createFileRoute('/_authorization-gate/tools/admin/pipeline-ops/')({
	validateSearch: searchSchema,
	staticData: {
		authorize: (ability) => ability.can('manage', 'devTools'),
		navigation: {
			label: 'Pipeline ops',
			keywords: ['pipeline', 'builds', 'ops', 'admin', 'progress'],
		},
	},
	head: () => ({ meta: [{ title: 'Pipeline ops • Cadstrom' }] }),
	component: AdminPipelineOpsPage,
});

/**
 * The staff pipeline-ops page: every active build on one dense screen, with enough on
 * each row to say whether it is progressing and enough on hover to say why not.
 */
function AdminPipelineOpsPage() {
	const trpc = useTRPC();
	const {
		windowDays,
		buildId: selectedBuildId,
		orgId,
		q: query,
		includeInternal,
		includeSuperseded,
		includeDormant,
		includeDelivered,
	} = Route.useSearch();
	const navigate = Route.useNavigate();
	const [filter, setFilter] = useState<PipelineOpsFilter>(PipelineOpsFilter.All);
	const [uploadRow, setUploadRow] = useState<PipelineOpsRow | null>(null);
	const [reviewRow, setReviewRow] = useState<PipelineOpsRow | null>(null);

	// The open drawer lives in the URL, not in component state: that is what makes a link
	// to one build's detail work on arrival, survive a refresh, and keep working when the
	// page is already mounted. Replaced rather than pushed, so scanning rows does not fill
	// the history with a step per row.
	const toggleBuild = useCallback(
		(buildId: string) => {
			void navigate({
				search: (previous) => ({ ...previous, buildId: previous.buildId === buildId ? undefined : buildId }),
				replace: true,
			});
		},
		[navigate],
	);

	const { data, dataUpdatedAt, refetch, isFetching } = useSuspenseQuery({
		// Only the flags actually set are sent: an absent one leaves the endpoint free to
		// read a named organization as a request for it, internal or not.
		...trpc.pipelineOps.getSnapshot.queryOptions({
			windowDays: windowDays ?? PIPELINE_OPS_DEFAULT_WINDOW_DAYS,
			...(orgId === undefined ? {} : { organizationId: orgId }),
			...(includeInternal === undefined ? {} : { includeInternalOrgs: includeInternal }),
			...(includeSuperseded === undefined ? {} : { includeSuperseded }),
			...(includeDormant === undefined ? {} : { includeDormant }),
			...(includeDelivered === undefined ? {} : { includeDelivered }),
		}),
		refetchInterval: REFETCH_INTERVAL_MS,
	});

	const included = useMemo(() => {
		const inclusions = new Set<PipelineOpsInclusion>();
		if (includeInternal === true) {
			inclusions.add(PipelineOpsInclusion.Internal);
		}
		if (includeSuperseded === true) {
			inclusions.add(PipelineOpsInclusion.Superseded);
		}
		if (includeDormant === true) {
			inclusions.add(PipelineOpsInclusion.Dormant);
		}
		if (includeDelivered === true) {
			inclusions.add(PipelineOpsInclusion.Delivered);
		}
		return inclusions;
	}, [includeInternal, includeSuperseded, includeDormant, includeDelivered]);

	const toggleInclusion = useCallback(
		(inclusion: PipelineOpsInclusion, include: boolean) => {
			// Written as undefined rather than false when switched off: the default view is the
			// one with no flags at all, and a link to it should not carry three of them.
			const value = include ? true : undefined;
			void navigate({
				search: (previous) => ({ ...previous, [SEARCH_KEY_BY_INCLUSION[inclusion]]: value }),
				replace: true,
			});
		},
		[navigate],
	);

	const handleAction = useCallback((row: PipelineOpsRow, action: PipelineOpsRowAction) => {
		if (action === PipelineOpsRowAction.UploadNetlist) {
			setUploadRow(row);
			return;
		}
		setReviewRow(row);
	}, []);

	const counts = useMemo(() => {
		const awaiting = data.builds.filter((build) => build.awaiting !== PipelineAwaitingState.None).length;
		const quiet = data.builds.filter((build) => heartbeatBars(build.heartbeat).quiet).length;
		const errors = data.stageCounts.find((stage) => stage.status === BuildStatus.Error)?.count ?? 0;
		const stalled = data.builds.filter(stalledBuild).length;
		return { awaiting, quiet, errors, stalled };
	}, [data]);

	const tileRows = useMemo(() => filterRows(data.builds, filter), [data.builds, filter]);
	const rows = useMemo(() => tileRows.filter((row) => rowMatchesSearch(row, query ?? '')), [tileRows, query]);
	return (
		<div className='flex min-h-0 grow flex-col gap-3 p-4 text-sm'>
			<div className='flex flex-wrap items-stretch gap-2'>
				<StatTile
					label='Active'
					value={data.activeBuilds}
					colorClass='text-gray-12'
					active={filter === PipelineOpsFilter.All}
					onClick={() => setFilter(PipelineOpsFilter.All)}
					title={`Every build in a non-terminal status in the last ${data.windowDays} days. The stage list beside these cards counts only the builds still in the pipeline, so failures are in Error alone.`}
				/>
				<StatTile
					label='Error'
					value={counts.errors}
					colorClass='text-red-a11'
					active={filter === PipelineOpsFilter.Error}
					onClick={() => setFilter(PipelineOpsFilter.Error)}
					title='Builds that failed and are waiting to be rescued'
				/>
				<StatTile
					label='Awaiting input'
					value={counts.awaiting}
					colorClass='text-orange-a11'
					active={filter === PipelineOpsFilter.Awaiting}
					onClick={() => setFilter(PipelineOpsFilter.Awaiting)}
					title='Builds whose workflow is blocked on an input — a netlist, a parse-error fix, an error review'
				/>
				<StatTile
					label='Quiet 3 h'
					value={counts.quiet}
					colorClass='text-amber-11'
					active={filter === PipelineOpsFilter.Quiet}
					onClick={() => setFilter(PipelineOpsFilter.Quiet)}
					title='Builds that have written nothing to their ledger for three hours — the strongest "not progressing" signal on the page'
				/>
				<StatTile
					label='No workflow'
					value={counts.stalled}
					colorClass='text-red-a11'
					active={filter === PipelineOpsFilter.Stalled}
					onClick={() => setFilter(PipelineOpsFilter.Stalled)}
					title='Builds the pipeline should be moving with no workflow running to move them: their design workflow has ended, they are merging their BOM with no merge running, or they are analysing with no analysis running'
				/>
				<StatTile
					label='Fast mode'
					value={data.fastMode.automatic}
					colorClass='text-amber-9'
					active={filter === PipelineOpsFilter.FastMode}
					onClick={() => setFilter(PipelineOpsFilter.FastMode)}
					title='Builds the system is advancing through the gates on its own'
				/>
				<StatTile
					label='Demoted'
					value={data.fastMode.demoted}
					colorClass='text-red-a10'
					active={filter === PipelineOpsFilter.Demoted}
					onClick={() => setFilter(PipelineOpsFilter.Demoted)}
					title='Builds that fell out of fast mode and are back on the manual process'
				/>
				<div className='flex flex-wrap items-center gap-x-3 gap-y-1 self-center pl-2 text-gray-a10'>
					{pipelinePositions(data.stageCounts).map((stage) => (
						<span key={stage.status} className='whitespace-nowrap'>
							<span className='text-gray-12 tabular-nums'>{stage.count}</span>{' '}
							{STATUS_LABELS[stage.status] ?? stage.status}
						</span>
					))}
				</div>
			</div>

			<div className='flex flex-wrap items-center gap-x-4 gap-y-1'>
				<OrganizationPicker
					organizations={data.organizations}
					value={orgId}
					onChange={(organizationId) => {
						void navigate({ search: (previous) => ({ ...previous, orgId: organizationId }), replace: true });
					}}
				/>
				<RowSearchBox
					value={query ?? ''}
					matches={rows.length}
					total={tileRows.length}
					onChange={(next) => {
						void navigate({
							search: (previous) => ({ ...previous, q: next === '' ? undefined : next }),
							replace: true,
						});
					}}
				/>
				<HiddenBuilds excluded={data.excluded} included={included} onToggle={toggleInclusion} />
				<UnlistedRuns runs={data.unlistedRuns} />
			</div>

			{data.truncated && (
				<p className='text-amber-11'>
					Showing the {data.builds.length} most recently moved builds of {data.activeBuilds}. Narrow the window to see
					the rest.
				</p>
			)}

			<div className='min-h-0 grow overflow-auto rounded-sm border border-gray-a5'>
				<table className='w-full border-collapse'>
					<thead className='sticky top-0 z-10 bg-gray-2 text-2xs text-gray-a10 uppercase'>
						<tr className='h-7 border-b border-gray-a5 text-left'>
							<th className='w-5' />
							<th className='px-1.5 font-normal'>project</th>
							<th className='px-1.5 font-normal'>status</th>
							<th className='px-1.5 font-normal'>progress</th>
							<th className='px-1.5 font-normal'>waiting on</th>
							<th className='px-1.5 font-normal'>mode</th>
							<th className='px-1.5 font-normal'>sla</th>
							<th className='px-1.5 font-normal'>funnel</th>
							<th className='px-1.5 font-normal'>analysis</th>
							<th className='px-1.5 font-normal'>24 h</th>
							<th className='px-1.5 text-right font-normal'>in status</th>
							<th className='px-1.5 text-right font-normal'>total</th>
							<th className='px-1.5 font-normal'>netlist</th>
						</tr>
					</thead>
					<tbody>
						{rows.map((row) => (
							<Fragment key={row.buildId}>
								<PipelineOpsGridRow
									row={row}
									stageClocks={data.stageClocks}
									expanded={row.buildId === selectedBuildId}
									onToggle={toggleBuild}
									onAction={handleAction}
								/>
								{row.buildId === selectedBuildId && (
									<tr>
										<td colSpan={GRID_COLUMN_COUNT} className='p-0'>
											<PipelineOpsDetail row={row} />
										</td>
									</tr>
								)}
							</Fragment>
						))}
					</tbody>
				</table>
				{rows.length === 0 && (
					<p className='p-4 text-gray-a10'>
						{query === undefined || query === '' ? 'Nothing matches this filter.' : `Nothing matches “${query}”.`}
					</p>
				)}
			</div>

			<div className='flex items-center justify-end gap-3 text-xs text-gray-a10'>
				<WindowPicker
					value={windowDays ?? PIPELINE_OPS_DEFAULT_WINDOW_DAYS}
					onChange={(days) => {
						void navigate({ search: (previous) => ({ ...previous, windowDays: days }), replace: true });
					}}
				/>
				<span>Refreshed {new Date(dataUpdatedAt).toLocaleTimeString()}</span>
				<Tooltip
					title={
						data.readModelComputedAt === undefined ?
							'The delivery read model has not run yet; stage and SLA judgments are unavailable.'
						:	`Stage, SLA and funnel judgments were computed at ${data.readModelComputedAt.toLocaleTimeString()} by the delivery read model, which every dashboard reads too.`
					}
				>
					<span>
						Judgments{' '}
						{data.readModelComputedAt === undefined ?
							'unavailable'
						:	`${formatAge(data.readModelComputedAt, data.computedAt)} old`}
					</span>
				</Tooltip>
				<Tooltip title='Refresh'>
					<IconButton
						size='small'
						disabled={isFetching}
						onClick={() => {
							void refetch();
						}}
					>
						<RefreshIcon fontSize='inherit' />
					</IconButton>
				</Tooltip>
			</div>

			{uploadRow !== null && <UploadDialog open row={uploadRow} onClose={() => setUploadRow(null)} />}
			{reviewRow !== null && <ReviewDialog open row={reviewRow} onClose={() => setReviewRow(null)} />}
		</div>
	);
}

/** The search key each inclusion is carried in. */
const SEARCH_KEY_BY_INCLUSION: Readonly<
	Record<PipelineOpsInclusion, 'includeInternal' | 'includeSuperseded' | 'includeDormant' | 'includeDelivered'>
> = {
	[PipelineOpsInclusion.Internal]: 'includeInternal',
	[PipelineOpsInclusion.Superseded]: 'includeSuperseded',
	[PipelineOpsInclusion.Dormant]: 'includeDormant',
	[PipelineOpsInclusion.Delivered]: 'includeDelivered',
};

/**
 * The stage counts that name a position in the pipeline.
 *
 * `error` is dropped: it is not somewhere a build is, it is something that happened to
 * one, and it already has a card of its own. Leaving it in both places asks the reader to
 * notice that the same build is being counted twice.
 *
 * @param stageCounts - Every status present in the snapshot.
 * @returns The stages to list beside the cards.
 */
function pipelinePositions(stageCounts: GetPipelineOpsSnapshotOutputDto['stageCounts']) {
	return stageCounts.filter((stage) => stage.status !== BuildStatus.Error);
}

/**
 * Narrows the grid to the builds a stat tile counts.
 *
 * @param builds - Every row in the snapshot.
 * @param filter - The tile the operator clicked.
 * @returns The rows to draw.
 */
function filterRows(builds: PipelineOpsRow[], filter: PipelineOpsFilter): PipelineOpsRow[] {
	switch (filter) {
		case PipelineOpsFilter.Error: {
			return builds.filter((build) => build.status === BuildStatus.Error);
		}
		case PipelineOpsFilter.Demoted: {
			return builds.filter((build) => build.automationMode === BuildAutomationMode.Demoted);
		}
		case PipelineOpsFilter.FastMode: {
			return builds.filter((build) => build.automationMode === BuildAutomationMode.Automatic);
		}
		case PipelineOpsFilter.Awaiting: {
			return builds.filter((build) => build.awaiting !== PipelineAwaitingState.None);
		}
		case PipelineOpsFilter.Quiet: {
			return builds.filter((build) => heartbeatBars(build.heartbeat).quiet);
		}
		case PipelineOpsFilter.Stalled: {
			return builds.filter(stalledBuild);
		}
		default: {
			return builds;
		}
	}
}
